/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import error from '../../../../common/nodejs/errors';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM: "confirm",
  EXECUTE: "execute",
  SUCCESS: "success",
  ERROR: "error",
  FAILED: "failed",
  EXECUTING_FAILED: "executing_failed",
  TIMEOUT: "timeout"
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement50013",
  data() {
    return {
      phase: PHASE.CONFIRM,
      isLoading: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      modal_requirement_50013: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.FAILED:
        case PHASE.TIMEOUT:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.CONFIRM:
        case PHASE.EXECUTE:
        case PHASE.EXECUTING_FAILED:
        default:
          return "secondary";
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 待機
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行
      const ExecutionTitle = 'individual.common_title_execution';
      // 成功
      const SuccessTitle = 'message.common_title_success';
      // エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        confirm: ConfirmationTitle,
        execute: ExecutionTitle,
        success: SuccessTitle,
        error: ErrorTitle,
        executing_failed: ConfirmationTitle,
        failed: ErrorTitle,
        timeout: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログの表示文言
     */
    workSupportMessage() {
      const messageMap = {
        confirm: this.$t(`individual.label_execute_confirm`, [this.$t(`${this.i18nWsKey}.menu_title`)]),
        execute: 'individual.label_wait',
        success: this.$t(`individual.label_Executed`, [this.$t(`${this.i18nWsKey}.menu_title`)]),
        failed: this.$t(`individual.label_Execution_Failed`, [this.$t(`${this.i18nWsKey}.menu_title`)]),
        executing_failed: 'individual.label_already_in_progress',
        error: 'individual.error_communication_error',
        timeout: 'individual.label_timed_out'
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : messageMap[this.phase];
    },

    // 「OK」ボタンの表示判定
    showButtonOk() {
      const showOkButtonMap = {
        confirm: true,
        success: true,
        failed: true,
        executing_failed: true,
        error: true,
        timeout: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.sessionProperty = SESSION_PROPERTY.DEFAULT,
      this.modal_requirement_50013 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_50013 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** NonDefaultSession遷移 */
    async standby() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 成功
              // 現在セッション更新（NonDefaultSession）
              this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
              // 実行処理
              await this.execute();
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 成功
              this.phase = PHASE.SUCCESS;
              break;
            case REQUEST_STATUS_FAILED:
              switch (response.data.async_code) {
                case error.WSP_50013_EXECUTING_ERROR:
                  // 実行中エラー
                  this.phase = PHASE.EXECUTING_FAILED;
                  break;
                case error.WSP_50013_TIMEOUT_ERROR:
                  // タイムアウトエラー
                  this.phase = PHASE.TIMEOUT;
                  break;
                case error.WSP_50013_EXECUTE_ERROR:
                case error.WSP_50013_NOT_EXECUTABLE_ERROR:
                  // 実行エラー
                  this.phase = PHASE.FAILED;
                  break;
                case error.WSP_50013_UNABLE_COMMUNICATE_ERROR:
                default:
                  // エラー
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate処理 */
    async terminate() {
      let isSuccess = false;
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'terminate'
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 成功
            isSuccess = true;
            // 現在セッション更新（DefaultSession）
            this.sessionProperty = SESSION_PROPERTY.DEFAULT;
            break;
          case REQUEST_STATUS_FAILED:
            if (this.phase === PHASE.ERROR) {
              // 共通エラー画面の場合は、ウィザードを閉じる
              break;
            } else {
              // 共通エラー
              this.showUnexpectedError();
              break;
            }
          default:
            // エラー
            this.showUnexpectedError();
            break;
        }
      }
      return isSuccess;
    },

    /** dispose処理 */
    async dispose() {
      // 実行結果にかかわらず終了
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'dispose'
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
    },

    /** 確認画面「OK」押下 */
    async clickConfirmOk() {
      // NonDefaultSession遷移
      this.phase = PHASE.EXECUTE;
      this.standby();
    },

    /** 確認画面「キャンセル」押下 */
    clickConfirmCancel() {
      this.closeWizard();
    },

    /** 成功画面、実行失敗画面、タイムアウト画面「OK」押下 */
    async clickOk() {
      try {
        this.isLoading = true;
        if (!await this.terminate()) {
          return;
        }
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** 共通エラー画面「OK」押下 */
    async clickErrorOk() {
      try {
        this.isLoading = true;
        if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
          await this.terminate();
        }
        // 共通エラー画面でOK押下時はterminateの処理結果に関わらずdisposeを実行後、機能終了する
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 「OK」ボタンの押下処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM:
          await this.clickConfirmOk();
          break;
        case PHASE.SUCCESS:
        case PHASE.FAILED:
        case PHASE.EXECUTING_FAILED:
        case PHASE.TIMEOUT:
          await this.clickOk();
          break;
        case PHASE.ERROR:
          await this.clickErrorOk();
          break;
        default:
          break;
      }
    }
  },

  /** DOM作成後実行処理 */
  async mounted() {
    this.init();
  }
};
